import { useMemo, useState } from "react";
import { useInboxConversation, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { htmlToText } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useInboxThreadDraft } from "../../hooks";
import { useInboxMessageDeleteMutations } from "../../../../hooks";
import { pendo } from "../../../../tracking";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../components/MessageCompositionFooter";
import { buildHtmlMessageWithFiles } from "../../utils";
import { EmailReplyModeControl, EmailReplyMode } from "./EmailReplyModeControl";

export interface InboxThreadEmailCompositionFooterProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxThreadEmailCompositionFooter = ({
  threadId,
  conversationData,
}: InboxThreadEmailCompositionFooterProps) => {
  const { t } = useTranslation("inbox");
  const { deleteEmailMutation } = useInboxMessageDeleteMutations();
  const [replyMode, setReplyMode] = useState<EmailReplyMode>("reply-all");
  const [composerKey, setComposerKey] = useState(0);

  const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
  const resolvedConversationData = conversationData ?? internalConversationData;
  const { messages, drafts, endusers } = resolvedConversationData;
  const metadata = resolvedConversationData.metadata;
  const enduserId = endusers.at(0)?.id;
  const latestMessage = messages.at(0);

  const { queryParams, setQueryParams, matchingDraft, isEditingScheduled, sendAtInPast, shouldQueryDraft } =
    useInboxThreadDraft({ conversationData: resolvedConversationData });

  const draftQuery = useTellescopeQuery({
    model: "emails",
    method: "getOne",
    request: queryParams.draftId ?? "",
    enabled: shouldQueryDraft,
  });

  const latestDraft = useMemo(() => {
    if (queryParams.draftId) {
      return matchingDraft?.content ?? draftQuery.data?.HTMLContent;
    }
    return drafts.at(0)?.content;
  }, [draftQuery.data, drafts, matchingDraft, queryParams.draftId]);

  const sendEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "createOne",
    onSuccess: () => {
      setComposerKey((k) => k + 1);
      setQueryParams((prev) => ({ ...prev, draftId: undefined }));
    },
  });

  const isMutating = sendEmailMutation.isPending || deleteEmailMutation.isPending;
  const canSend = !!enduserId && !!metadata?.title && !isMutating;

  const buildPayload = (
    { message, files }: MessageCompositionFooterPayload,
    resolvedEnduserId: string,
    overrides?: { sendAt?: Date }
  ) => {
    const cc = replyMode === "reply-all" && latestMessage?.cc ? latestMessage.cc : undefined;
    const html = buildHtmlMessageWithFiles(message, files);
    return {
      replyTo: latestMessage?.emailMessageId,
      enduserId: resolvedEnduserId,
      isMarketing: false,
      HTMLContent: html,
      textContent: htmlToText(html),
      subject: `Re: ${metadata?.title}`,
      inbound: false,
      ...(cc && { cc }),
      ...overrides,
    };
  };

  return (
    <MessageCompositionFooter
      key={composerKey}
      editorVariant="richtext"
      channelType="email"
      placeholder={t("Write a reply...")}
      submitOnEnter={false}
      initialMessage={latestDraft}
      canSend={canSend}
      isUpdatePending={isMutating}
      enduserId={enduserId}
      isEditingScheduled={isEditingScheduled}
      sendAtInPast={sendAtInPast}
      initDate={matchingDraft?.sendAt}
      scheduleDialogKey={matchingDraft?.sendAt?.toISOString() ?? "no-init-date"}
      trailingActions={
        !isEditingScheduled && (
          <EmailReplyModeControl
            replyMode={replyMode}
            onChangeReplyMode={setReplyMode}
            forwardEmailId={latestMessage?.id}
            disabled={sendEmailMutation.isPending}
          />
        )
      }
      onSubmit={(payload) => {
        if (!enduserId || !metadata?.title) return;
        pendo.track("messaging-email-thread-compose-send");
        const draftToDelete = matchingDraft;
        sendEmailMutation.mutate(buildPayload(payload, enduserId), {
          onSuccess: () => {
            if (draftToDelete) deleteEmailMutation.mutate(draftToDelete.id);
          },
        });
      }}
      onSchedule={(payload, sendAt) => {
        if (!enduserId || !metadata?.title) return;
        if (matchingDraft) {
          const draftId = matchingDraft.id;
          deleteEmailMutation.mutate(draftId, {
            onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt })),
          });
        } else {
          sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt }));
        }
      }}
      onSendNow={(payload) => {
        if (!matchingDraft || !enduserId || !metadata?.title) return;
        const draftId = matchingDraft.id;
        deleteEmailMutation.mutate(draftId, {
          onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt: new Date() })),
        });
      }}
      onSaveScheduled={(payload) => {
        if (!matchingDraft || !enduserId || !metadata?.title) return;
        const draft = matchingDraft;
        deleteEmailMutation.mutate(draft.id, {
          onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt: draft.sendAt })),
        });
      }}
      onReschedule={(payload, sendAt) => {
        if (!matchingDraft || !enduserId || !metadata?.title) return;
        const draftId = matchingDraft.id;
        deleteEmailMutation.mutate(draftId, {
          onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt })),
        });
      }}
      trackingIds={{
        schedule: "email-thread-schedule-message-dialog-open",
        reschedule: "email-thread-reschedule-message-dialog-open",
      }}
    />
  );
};
